import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './VisibilitySelect.css';

export default function VisibilitySelect({
  value = 'everyone',
  onChange,
  disabled = false,
  options = [
    { value: 'admins', label: 'Admins Only', icon: 'fas fa-shield-alt' },
    { value: 'everyone', label: 'Everyone', icon: 'fas fa-globe' },
  ],
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, minWidth: 140 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      minWidth: Math.max(rect.width, 140),
    });
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleScrollOrResize = () => {
        updatePosition();
      };
      const handleClickOutside = (event) => {
        if (
          triggerRef.current &&
          !triggerRef.current.contains(event.target) &&
          menuRef.current &&
          !menuRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  const selectedOption = options.find((o) => o.value === value) || options[0];

  const handleSelect = (val, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onChange) {
      onChange(val);
    }
    setIsOpen(false);
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div
      className={`visibility-select-container ${isOpen ? 'is-open' : ''} ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`visibility-select-btn ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="visibility-select-label">{selectedOption.label}</span>
        <i className={`fas fa-chevron-down visibility-select-chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="visibility-select-menu"
            role="listbox"
            style={{
              position: 'absolute',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              minWidth: `${menuPosition.minWidth}px`,
              zIndex: 99999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`visibility-select-item ${isSelected ? 'selected' : ''}`}
                  onClick={(e) => handleSelect(opt.value, e)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="visibility-select-item-content">
                    {opt.icon && <i className={`${opt.icon} visibility-select-item-icon`} />}
                    <span>{opt.label}</span>
                  </div>
                  {isSelected && <i className="fas fa-check visibility-select-item-check" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
